/* GASSISA
=========================================================================================
* Descripcion: Lector de codigos de barras exclusivo para formularios portatiles.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 26/06/2026 | Lic. Jose Luis Aragon Cervantes  | Creacion sin captura global de foco
 *      2       | 23/09/2026 | LIC. José Luis Aragón C.                       | Deshabilita autocomplete del lector
 *      3       | 08/10/2026 | LIC. José Luis Aragón C.                       | Icono camara opcional junto al input
 *              |            |                                  | (abre lector por camara en iOS/Android)
 * ---------------------------------------------------------------------------------------
 */

import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ContentPaste from "@mui/icons-material/ContentPaste";
import { leerTextoPortapapeles } from "../../../helpers/portapapeles";
import { useSesion } from "../../../context/System/SesionContext";
import { useVentas } from "../../../context/Ventas/VentasContext";

const normalizarCodigo = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .toUpperCase();

const SizeSoftXLectorCodigoBarrasPortatil = forwardRef(function SizeSoftXLectorCodigoBarrasPortatil(
  { TipoVenta = "PREVENTA", disabled = false, mostrarBotonCamara = false, onAbrirCamara = null },
  ref
) {
  const inputRef = useRef(null);
  const codigoRef = useRef("");
  const [codigo, setCodigo] = useState("");
  const { parametrosCajaForm } = useSesion();
  const {
    handleRegistrarArticuloSubmitVenta,
    handleRegistrarArticuloSubmitPreVenta,
    handleArirModalBuscarArticulos,
    handleArirModalFirmarVenta,
    handleArirModalCobrarVenta,
    handleClickEliminarPartidaVentas,
    handleClickEliminarVenta,
    handleBuscarClienteFromClave,
    handleAbrirValidarCliente,
    partidaSeleccionada,
    barcodeAnterior,
    statusKeyActions = false,
  } = useVentas();

  const setCodigoValue = (value) => {
    codigoRef.current = value;
    setCodigo(value);
  };

  const focusAndSelect = () => {
    inputRef.current?.focus?.({ preventScroll: true });
    inputRef.current?.select?.();
  };

  const handlePegarPortapapeles = async () => {
    if (statusKeyActions || disabled) return;
    try {
      const texto = await leerTextoPortapapeles();
      if (texto) setCodigoValue(normalizarCodigo(texto));
    } catch {}
    requestAnimationFrame(focusAndSelect);
  };

  useImperativeHandle(ref, () => ({
    focus: focusAndSelect,
    focusAndSelect,
    clear: () => setCodigoValue(""),
    getValue: () => codigoRef.current,
  }));

  const registrarCodigo = async (codigoBarras) => {
    if (TipoVenta === "VENTA") {
      await handleRegistrarArticuloSubmitVenta(codigoBarras, parametrosCajaForm.dominio);
    } else {
      await handleRegistrarArticuloSubmitPreVenta(codigoBarras, parametrosCajaForm.dominio);
    }

    setCodigoValue("");
    requestAnimationFrame(focusAndSelect);
  };

  const handleKeyDown = async (event) => {
    if (statusKeyActions || disabled) return;

    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const codigoBarras = normalizarCodigo(codigoRef.current);

      if (codigoBarras.includes("+")) {
        const repeticiones = Number.parseInt(codigoBarras.replace("+", ""), 10);
        if (barcodeAnterior?.length >= 5 && Number.isInteger(repeticiones) && repeticiones > 0) {
          for (let index = 0; index < repeticiones; index += 1) {
            await registrarCodigo(barcodeAnterior);
          }
        }
        setCodigoValue("");
        requestAnimationFrame(focusAndSelect);
        return;
      }

      if (codigoBarras.includes(".'LAUNION'.")) {
        handleAbrirValidarCliente();
        setCodigoValue("");
        return;
      }

      if (codigoBarras) await registrarCodigo(codigoBarras);
      return;
    }

    if (event.altKey && event.key.trim().toUpperCase() === "F1") {
      event.preventDefault();
      handleArirModalBuscarArticulos();
      setCodigoValue("");
      return;
    }

    if (event.altKey && event.key.trim().toUpperCase() === "F10") {
      event.preventDefault();
      if (parametrosCajaForm.tipoventa === "VENTA") handleArirModalCobrarVenta();
      else handleArirModalFirmarVenta();
      return;
    }

    if (event.ctrlKey && event.key.trim().toUpperCase() === "D") {
      event.preventDefault();
      handleBuscarClienteFromClave("0");
      return;
    }

    if (event.altKey && event.key.trim().toUpperCase() === "F7") {
      event.preventDefault();
      handleClickEliminarPartidaVentas(partidaSeleccionada?.idpartida);
      return;
    }

    if (event.key === "F12") {
      event.preventDefault();
      handleClickEliminarVenta();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setCodigoValue("");
    }
  };

  return (
    <div className="framedetallesventa lector-codigo-portatil">
      <h2 className="framedetallesventaLabel">Lector códigos</h2>
      <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}>
        <input
          id="txtCodigoBarrasPortatil"
          className="txtCodigoBarras"
          ref={inputRef}
          type="text"
          inputMode="text"
          enterKeyHint="enter"
          autoComplete="off"
          name="lector-codigo-barras"
          aria-autocomplete="none"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          value={codigo}
          onChange={(event) => setCodigoValue(normalizarCodigo(event.target.value))}
          onKeyDown={handleKeyDown}
          placeholder="Escanear código de barras"
          disabled={statusKeyActions || disabled}
          style={{ flex: 1, minWidth: 0 }}
        />
        {mostrarBotonCamara && (
          <IconButton
            color="primary"
            size="small"
            title="Escanear con cámara"
            aria-label="Escanear con cámara"
            onClick={() => onAbrirCamara?.()}
            disabled={statusKeyActions || disabled}
            sx={{ flex: "0 0 auto", border: "1px solid rgba(0,0,0,0.12)" }}
          >
            <CameraAltIcon fontSize="small" />
          </IconButton>
        )}
        <Tooltip title="Pegar">
          <span>
            <IconButton
              color="primary"
              size="small"
              title="Pegar"
              aria-label="Pegar"
              onClick={handlePegarPortapapeles}
              disabled={statusKeyActions || disabled}
              sx={{ flex: "0 0 auto", border: "1px solid rgba(0,0,0,0.12)" }}
            >
              <ContentPaste fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
});

export default SizeSoftXLectorCodigoBarrasPortatil;
